import { useEffect, useRef, useState } from "react";
import type { Kind, Node, Space, View, ViewFilters } from "@melo/shared";
import { DISCIPLINES, DISCIPLINE_LABELS } from "@melo/shared/client";
import { store, useTables } from "../lib/store";
import { agents, byRank, formatDate, isDone, people, todayIso } from "../lib/select";
import { filterCount, isEmpty, sameFilters, toggleIn } from "../lib/filters";
import { send } from "../lib/actions";
import { can } from "../lib/access";
import { toast } from "../lib/toast";
import { useShortcut } from "../lib/keys";
import { lower, milestoneWord, plural } from "../lib/words";
import { Avatar, Popover } from "./primitives";
import { MenuButton, StatusDot } from "./pickers";
import { Icon } from "./icons";

const DUE_LABEL: Record<string, string> = { overdue: "Overdue", week: "Due this week", none: "No due date" };

type Props = { space: Space; view: View; kinds: Kind[]; filters: ViewFilters; setFilters: (f: ViewFilters | null) => void; overridden: boolean };

/** The project's milestones, soonest first, with the one in progress marked. */
export function milestonesOf(t: ReturnType<typeof useTables>, spaceId: string): Node[] {
  return [...t.nodes.values()]
    .filter((n) => n.spaceId === spaceId && !n.draft && t.kinds.get(n.kindId)?.key === "phase")
    .sort((a, b) => (a.dueDate ?? a.startDate ?? "9999").localeCompare(b.dueDate ?? b.startDate ?? "9999") || byRank(a, b));
}

/** The milestone happening now: the earliest unfinished one whose dates include today, or else the next one due. */
export function currentMilestone(t: ReturnType<typeof useTables>, spaceId: string): Node | undefined {
  const today = todayIso();
  const open = milestonesOf(t, spaceId).filter((m) => !isDone(t, m));
  return open.find((m) => (m.startDate ?? "0000") <= today && (m.dueDate ?? "9999") >= today) ?? open.find((m) => (m.dueDate ?? "9999") >= today) ?? open[0];
}

/**
 * The tools on the right of the view tabs: which milestone you are looking at, a title search,
 * and one Filter button. Filters live in the URL, so a filtered view is a link you can share.
 */
export function ViewTools({ space, view, kinds, filters, setFilters, children }: Props & { children?: React.ReactNode }) {
  const t = useTables();
  const [text, setText] = useState(filters.text ?? "");
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => setText(filters.text ?? ""), [filters.text]);
  useEffect(() => {
    const id = setTimeout(() => {
      if ((filters.text ?? "") !== text) setFilters({ ...filters, text: text || undefined });
    }, 200);
    return () => clearTimeout(id);
    // Only the typed text drives this; the other filters are read at the moment it fires.
  }, [text]);
  useShortcut("f", () => search.current?.focus());
  const milestones = milestonesOf(t, space.id);
  const now = currentMilestone(t, space.id);
  const scope = filters.milestone ? t.nodes.get(filters.milestone) : undefined;
  const showScope = milestones.length > 0 && !view.config.rootId && view.type !== "timeline";
  const word = milestoneWord(t, space.id);

  return (
    <div className="vtools">
      {showScope && (
        <MenuButton
          label={word}
          className={`btn sm${scope ? " on" : " ghost"}`}
          searchable={milestones.length > 8}
          width={300}
          items={() => [
            { id: "all", label: `All ${lower(plural(word))}`, checked: !scope, run: () => setFilters({ ...filters, milestone: undefined }) },
            ...milestones.map((m) => ({
              id: m.id,
              label: m.title,
              icon: <Icon name="flag" />,
              hint: m.id === now?.id ? "Now" : m.dueDate ? formatDate(m.dueDate) : undefined,
              checked: m.id === scope?.id,
              run: () => setFilters({ ...filters, milestone: m.id }),
            })),
          ]}
        >
          <Icon name="flag" />
          <span className="vt-scope">{scope ? scope.title : `All ${lower(plural(word))}`}</span>
          <Icon name="down" />
        </MenuButton>
      )}
      {children}
      <label className="vt-search">
        <Icon name="search" />
        <input ref={search} value={text} onChange={(e) => setText(e.target.value)} placeholder="Search" aria-label="Filter by title" onKeyDown={(e) => e.key === "Escape" && (setText(""), (e.target as HTMLInputElement).blur())} />
      </label>
      <FilterButton space={space} kinds={kinds} filters={filters} setFilters={setFilters} />
    </div>
  );
}

function FilterButton({ space, kinds, filters, setFilters }: Pick<Props, "space" | "kinds" | "filters" | "setFilters">) {
  const t = useTables();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const count = filterCount(filters);
  const members = new Set([...t.members.values()].filter((m) => m.spaceId === space.id).map((m) => m.actorId));
  // People on the project first; agents only when they own work here.
  const agentOwners = new Set([...t.nodes.values()].filter((n) => n.spaceId === space.id).flatMap((n) => n.assignees.map((a) => a.actorId)));
  const folks = [...people(t).filter((a) => members.has(a.id) || a.role === "admin"), ...agents(t).filter((a) => agentOwners.has(a.id))];
  const labels = [...t.labels.values()].filter((l) => l.spaceId === space.id).sort(byRank);
  const statuses = statusOptions(kinds);
  const priorityDef = kinds.flatMap((k) => k.fields).find((f) => f.key === "priority");
  const hasDiscipline = kinds.some((k) => k.fields.some((f) => f.key === "discipline"));
  const on = (key: "assignees" | "labels" | "statuses" | "priorities" | "disciplines", v: string) => (filters[key] ?? []).includes(v);
  const toggle = (key: "assignees" | "labels" | "statuses" | "priorities" | "disciplines", v: string) => setFilters(toggleIn(filters, key, v));
  const chip = (key: "assignees" | "labels" | "statuses" | "priorities" | "disciplines", v: string, body: React.ReactNode) => (
    <button key={v} type="button" className="fchip" aria-pressed={on(key, v)} onClick={() => toggle(key, v)}>
      {body}
    </button>
  );
  return (
    <>
      <button ref={ref} type="button" className={`btn sm${count ? " on" : " ghost"}`} aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)}>
        <Icon name="filter" /> Filter{count ? <span className="vt-count">{count}</span> : null}
      </button>
      {open && (
        <Popover anchor={ref.current} onClose={() => setOpen(false)} align="end" width={360}>
          <div className="fpanel" role="dialog" aria-label="Filters">
            <FGroup title="Person">
              {chip("assignees", "me", <><Avatar actor={store.me ?? undefined} size="sm" /> Me</>)}
              {chip("assignees", "none", <><Icon name="user" /> No one</>)}
              {folks.filter((a) => a.id !== store.me?.id).map((a) => chip("assignees", a.id, <><Avatar actor={a} size="sm" /> {a.name}</>))}
            </FGroup>
            {labels.length > 0 && <FGroup title="Label">{labels.map((l) => chip("labels", l.id, <><span className="label-dot" style={{ background: l.color }} /> {l.name}</>))}</FGroup>}
            <FGroup title="Status">{statuses.map((s) => chip("statuses", s.key, <><StatusDot status={s.def} /> {s.label}</>))}</FGroup>
            {priorityDef && <FGroup title="Priority">{[...(priorityDef.options ?? []), "none"].map((p) => chip("priorities", p, p === "none" ? "None" : `${p[0]!.toUpperCase()}${p.slice(1)}`))}</FGroup>}
            {hasDiscipline && <FGroup title="Discipline">{[...DISCIPLINES, "none"].map((d) => chip("disciplines", d, d === "none" ? "None" : DISCIPLINE_LABELS[d]!))}</FGroup>}
            <FGroup title="Due">
              {(["overdue", "week", "none"] as const).map((d) => (
                <button key={d} type="button" className="fchip" aria-pressed={filters.due === d} onClick={() => setFilters({ ...filters, due: filters.due === d ? undefined : d })}>
                  {DUE_LABEL[d]}
                </button>
              ))}
            </FGroup>
            <div className="fpanel-foot">
              <span className="hint grow">Items that match every group are shown.</span>
              <button type="button" className="btn ghost sm" disabled={!count} onClick={() => setFilters({ ...filters, assignees: undefined, labels: undefined, statuses: undefined, priorities: undefined, disciplines: undefined, due: undefined })}>
                Clear
              </button>
            </div>
          </div>
        </Popover>
      )}
    </>
  );
}

function FGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="fgroup" role="group" aria-label={title}>
      <span className="fgroup-t">{title}</span>
      <div className="fgroup-b">{children}</div>
    </div>
  );
}

function statusOptions(kinds: Kind[]) {
  return kinds.length === 1
    ? kinds[0]!.statuses.map((s) => ({ key: s.key, label: s.label, def: s }))
    : (["todo", "active", "review", "done"] as const).map((c) => ({ key: `cat:${c}`, label: { todo: "To do", active: "In progress", review: "In review", done: "Done" }[c], def: { key: c, label: c, category: c } }));
}

/** The filters in force, as removable chips, with Save and Reset when they differ from the view's own. Hidden when nothing is on. */
export function FilterChips({ space, view, kinds, filters, setFilters, overridden }: Props) {
  const t = useTables();
  const statuses = statusOptions(kinds);
  const chips: { key: string; text: string; remove: () => void }[] = [];
  for (const a of filters.assignees ?? []) chips.push({ key: `a${a}`, text: a === "me" ? "Assigned to me" : a === "none" ? "Unassigned" : (t.actors.get(a)?.name ?? "Someone"), remove: () => setFilters(toggleIn(filters, "assignees", a)) });
  for (const l of filters.labels ?? []) chips.push({ key: `l${l}`, text: t.labels.get(l)?.name ?? "Label", remove: () => setFilters(toggleIn(filters, "labels", l)) });
  for (const s of filters.statuses ?? []) chips.push({ key: `s${s}`, text: statuses.find((x) => x.key === s)?.label ?? s, remove: () => setFilters(toggleIn(filters, "statuses", s)) });
  for (const p of filters.priorities ?? []) chips.push({ key: `p${p}`, text: p === "none" ? "No priority" : `${p[0]!.toUpperCase()}${p.slice(1)} priority`, remove: () => setFilters(toggleIn(filters, "priorities", p)) });
  for (const d of filters.disciplines ?? []) chips.push({ key: `d${d}`, text: DISCIPLINE_LABELS[d] ?? d, remove: () => setFilters(toggleIn(filters, "disciplines", d)) });
  if (filters.due) chips.push({ key: "due", text: DUE_LABEL[filters.due]!, remove: () => setFilters({ ...filters, due: undefined }) });
  // Looking at another milestone or searching by title is navigation, not a change to the view worth saving.
  const dirty = overridden && !sameFilters({ ...filters, milestone: undefined, text: undefined }, { ...view.config.filters, milestone: undefined, text: undefined });
  if (!chips.length && !dirty) return null;
  return (
    <div className="fchips" aria-label="Filters in use">
      {chips.map((c) => (
        <span key={c.key} className="chip agent">
          {c.text}
          <button type="button" aria-label={`Remove filter ${c.text}`} onClick={c.remove}>
            <Icon name="x" size={12} />
          </button>
        </span>
      ))}
      {chips.length > 0 && (
        <button type="button" className="btn ghost sm" onClick={() => setFilters(isEmpty(view.config.filters) ? null : { milestone: filters.milestone })}>
          Clear
        </button>
      )}
      {dirty && can(space.id, "member") && (
        <span className="fchips-save">
          <button type="button" className="btn ghost sm" onClick={() => setFilters(null)}>
            Reset
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              void send("view.upsert", { id: view.id, spaceId: space.id, name: view.name, type: view.type, config: { filters: { ...filters, text: undefined } } }).then(() => {
                setFilters(null);
                toast(`Saved these filters to ${view.name}`);
              })
            }
          >
            Save to view
          </button>
        </span>
      )}
    </div>
  );
}
